// @ts-nocheck
import { useState, useCallback } from 'react'
import { Phone, Clock, PhoneIncoming, PhoneCall, AlertCircle, CheckCircle2, RefreshCw, XCircle, Pause } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useCallStore } from '../stores/callStore'
import { useApi } from '../hooks/useApi'
import { useTwilioDevice } from '../hooks/useTwilioDevice'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'
import HoldIndicator from '../components/HoldIndicator'
import { logger } from '../utils/logger'

export default function QueuePage() {
  // Zustand Selector最適化: 必要な値のみを個別に取得
  const user = useAuthStore((state) => state.user)
  const callStatus = useCallStore((state) => state.callStatus)
  const queuedCalls = useCallStore((state) => state.queuedCalls)
  const heldCalls = useCallStore((state) => state.heldCalls)
  const api = useApi()

  // 自分が保留にした通話のみ（HoldIndicator表示用）
  const myHeldCalls = heldCalls.filter(call => call.operatorUserId === user?.sub)

  // ブラウザ受話処理用のフック
  const { answerQueueCall, resumeHeldCall } = useTwilioDevice()

  // isAnswerPending相当: callStatus === 'connecting' で代替
  const isConnecting = callStatus === 'connecting'

  const [ignoredCallIds, setIgnoredCallIds] = useState([]) // 無視した着信のID
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // 手動更新（サーバーからキューを再取得）
  const handleManualRefresh = useCallback(async () => {
    logger.debug('[QueuePage] 手動更新を実行')
    setLoading(true)
    try {
      const data = await api.get('/api/queue/calls')
      logger.info('[QueuePage] 手動更新完了:', data.items?.length || 0, '件')
      setError(null)
    } catch (err) {
      logger.error('手動更新エラー:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [api])

  // 着信に応答（転送方式）
  const handleAnswerCall = async (call) => {
    try {
      if (!call.callSid) {
        throw new Error('callSidが存在しません')
      }

      logger.debug('[QueuePage] 着信応答開始:', call.callSid)

      await answerQueueCall(call.callSid)

      logger.info('[QueuePage] 着信応答成功')
      showNotification('success', '着信に応答しました')

    } catch (err) {
      logger.error('[QueuePage] 応答エラー:', err)

      if (err.message && err.message.includes('他のオペレータ')) {
        showNotification('error', err.message)
      } else if (err.message && err.message.includes('終了')) {
        showNotification('error', '通話は既に終了しています')
      } else {
        showNotification('error', `エラー: ${err.message || '受話処理に失敗しました'}`)
      }
    }
  }

  // 着信を拒否
  const handleRejectCall = async (call) => {
    if (!confirm('この着信を拒否しますか？')) {
      return
    }

    try {
      await api.post(`/api/queue/calls/${call.callId}/reject`, {
        reason: 'operator_rejected',
      })

      showNotification('success', '着信を拒否しました')
    } catch (err) {
      logger.error('拒否エラー:', err)
      showNotification('error', `エラー: ${err.message}`)
    }
  }

  // 着信を無視（他のオペレーターが対応可能）
  const handleIgnoreCall = (call) => {
    if (!confirm('この着信を無視しますか？\n（他のオペレーターが応答できます）')) {
      return
    }

    setIgnoredCallIds(prev => [...prev, call.callId])
    showNotification('success', '着信を無視しました（他のオペレーターが対応できます）')
  }

  // 通知を表示（簡易版）
  const showNotification = (type, message) => {
    alert(message)
  }

  // 待機時間を計算
  const formatWaitTime = (queuedAt) => {
    const seconds = Math.floor((Date.now() - queuedAt) / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // 待機時間の色を取得
  const getWaitTimeColor = (queuedAt) => {
    const seconds = Math.floor((Date.now() - queuedAt) / 1000)
    if (seconds < 30) return 'text-green-600 dark:text-green-400'
    if (seconds < 60) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-red-600 dark:text-red-400'
  }

  // 保留時間をフォーマット
  const formatHoldDuration = (heldAt) => {
    const seconds = Math.floor((Date.now() - heldAt) / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
      return `${hours}時間${minutes % 60}分`
    }
    return `${minutes}分${seconds % 60}秒`
  }

  // 保留中の通話を受話
  const handleResumeHeldCall = async (holdQueueName) => {
    try {
      logger.debug('[QueuePage] 保留解除開始:', holdQueueName)

      await resumeHeldCall(holdQueueName)

      logger.info('[QueuePage] 保留解除成功')

    } catch (err) {
      logger.error('[QueuePage] 保留解除エラー:', err)

      if (err.message && err.message.includes('他のオペレータ')) {
        showNotification('error', err.message)
      } else if (err.message && err.message.includes('終了')) {
        showNotification('error', '通話は既に終了しています')
      } else {
        showNotification('error', `エラー: ${err.message || '保留解除に失敗しました'}`)
      }
    }
  }

  // 無視した着信を除外してソート（長い順）
  const sortedCalls = queuedCalls
    .filter(call => !ignoredCallIds.includes(call.callId))
    .sort((a, b) => a.queuedAt - b.queuedAt)

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <PhoneCall className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            着信キュー
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            待機中の着信を管理します
          </p>
        </div>

        {/* コントロール */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>更新</span>
          </button>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-red-800 dark:text-red-200 text-sm font-medium">
              {error}
            </p>
          </div>
        </div>
      )}

      {/* 統計サマリー */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">待機中の着信</p>
              <p className="text-4xl font-bold">{queuedCalls.length}</p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <PhoneIncoming className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium mb-1">最長待機時間</p>
              <p className="text-4xl font-bold">
                {queuedCalls.length > 0
                  ? formatWaitTime(Math.min(...queuedCalls.map(c => c.queuedAt)))
                  : '0:00'}
              </p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Clock className="w-8 h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium mb-1">自動更新</p>
              <p className="text-2xl font-bold">5秒毎</p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <RefreshCw className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* 着信リスト */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            着信一覧 ({sortedCalls.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading && sortedCalls.length === 0 ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">
                読み込み中...
              </p>
            </div>
          ) : sortedCalls.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 dark:text-green-400 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                待機中の着信はありません
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                新しい着信があると、ここに表示されます
              </p>
            </div>
          ) : (
            sortedCalls.map((call, index) => (
              <div key={call.callId} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center justify-between gap-6">
                  {/* 順番 */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                      <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {index + 1}
                      </span>
                    </div>
                  </div>

                  {/* 通話情報 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <PhoneIncoming className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <span className="text-xl font-bold text-gray-900 dark:text-white truncate">
                        {formatPhoneNumberForDisplay(call.from)}
                      </span>
                    </div>
                    {/* 着信先電話番号の名前を目立つように表示 */}
                    {call.phoneNumberName && (
                      <div className="mb-2">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-base rounded-lg shadow-md">
                          <Phone className="w-5 h-5" />
                          着信先: {call.phoneNumberName}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      {!call.phoneNumberName && (
                        <span>着信先: {formatPhoneNumberForDisplay(call.to)}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        Call SID: {call.twilioCallSid?.substring(0, 20)}...
                      </span>
                    </div>
                  </div>

                  {/* 待機時間 */}
                  <div className="flex-shrink-0 text-right">
                    <div className={`text-2xl font-bold ${getWaitTimeColor(call.queuedAt)}`}>
                      {formatWaitTime(call.queuedAt)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-end gap-1 mt-1">
                      <Clock className="w-4 h-4" />
                      待機時間
                    </div>
                  </div>

                  {/* アクションボタン */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleAnswerCall(call)}
                      disabled={isConnecting}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                    >
                      {isConnecting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          処理中...
                        </>
                      ) : (
                        <>
                          <Phone className="w-5 h-5" />
                          応答
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleRejectCall(call)}
                      disabled={isConnecting}
                      className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="w-5 h-5" />
                      拒否
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 保留中の通話一覧 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Pause className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            保留中の通話 ({heldCalls.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {heldCalls.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                保留中の通話はありません
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                保留中の通話があると、ここに表示されます
              </p>
            </div>
          ) : (
            heldCalls.map((call) => (
              <div key={call.holdQueueName} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center justify-between gap-6">
                  {/* アイコン */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center">
                      <Pause className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                    </div>
                  </div>

                  {/* 通話情報 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Phone className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                      <span className="text-xl font-bold text-gray-900 dark:text-white truncate">
                        {formatPhoneNumberForDisplay(call.callerNumber)}
                      </span>
                      {call.customerName && (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          ({call.customerName})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>保留者: {call.operatorName}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        保留時間: {formatHoldDuration(call.heldAt)}
                      </span>
                    </div>
                  </div>

                  {/* 受話ボタン */}
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleResumeHeldCall(call.holdQueueName)}
                      disabled={isConnecting}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                    >
                      {isConnecting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          処理中...
                        </>
                      ) : (
                        <>
                          <Phone className="w-5 h-5" />
                          受話
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 左下固定: 保留インジケーター（自分の保留通話のみ） */}
      <HoldIndicator
        calls={myHeldCalls}
        onResume={resumeHeldCall}
      />
    </div>
  )
}
